'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { UsdtCurrency } from '@flipster/utils/constants/currency';
import Num, { formatToShortNumber } from '@flipster/utils/num';
import webviewHandler from '@flipster/utils/webview-handler';

import { CurrencyDisplay } from '../../../../../misc/display';
import { useLoginKycGuard } from '../../../../../shared/login-kyc-gurad';
import { defiYieldsOverviewBunja } from '../../../../shared/state';
import { useProductConfig } from '../../product-config';

export const SubscriptionFooter = () => {
  const { detailsAtom, openSubscribeModal, subscribeDeepLinkPath } = useProductConfig();
  const guardLoginAndKyc = useLoginKycGuard();
  const details = useAtomValue(detailsAtom);
  const { isPendingAtom } = useBunja(defiYieldsOverviewBunja);
  const isOverviewPending = useAtomValue(isPendingAtom);

  const totalCap = Num(details?.subscriptionTotalCap ?? '0');
  const filledPercent = totalCap.isZero()
    ? '0'
    : Num(details?.subscriptionFilledCap ?? '0')
        .dividedBy(totalCap)
        .multipliedBy(100)
        .toFixed(2);

  return (
    <div className={stack({ gap: 16 })}>
      <div className={stack({ gap: 10 })}>
        <div className={hstack({ justify: 'space-between' })}>
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
            <FormattedMessage id='Earn.r251208.Total_subscription_cap' />
          </p>
          <p className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-5' })}>
            {`${formatToShortNumber(details?.subscriptionFilledCap ?? '0')} / ${formatToShortNumber(details?.subscriptionTotalCap ?? '0')} USDT (${filledPercent}%)`}
          </p>
        </div>
        <div className={css({ rounded: 1000, bg: 'background.default.level-3', pos: 'relative', h: 6 })}>
          <div
            className={css({
              pos: 'absolute',
              top: 0,
              left: 0,
              h: '100%',
              rounded: 1000,
              bg: 'inverse.white',
            })}
            style={{ width: `${filledPercent}%` }}
          />
        </div>
      </div>
      <Button
        size='large'
        hierarchy='primary'
        css={{ w: '100%' }}
        disabled={isOverviewPending}
        onClick={() => {
          guardLoginAndKyc(() => {
            webviewHandler.postMessage({ action: 'link', params: { page: subscribeDeepLinkPath } }, () =>
              openSubscribeModal(),
            );
          });
        }}
        label={
          <div className={hstack()}>
            <CurrencyDisplay currency={UsdtCurrency} icon={<CurrencyDisplay.Icon size={20} />} />
            <FormattedMessage id='Earn.r251204.Enter_subscription_amount' />
          </div>
        }
      />
    </div>
  );
};
